// web-vite/src/screens/PersonnelRoster.tsx
//
// Mirrors src/screens/personnel/PersonnelListScreen.tsx — 4-column table:
//   Avatar | Name & Emp ID | Tag ID & Status | Other Tags | Actions
// plus a SearchBar + 3 SummaryCards above it.
//
// V1 is read-only: no row-tap navigation, no "Add Personnel" button. P3.5
// can extend this with a detail panel slide-in if needed.

import React, {useMemo, useState} from 'react'
import clsx from 'clsx'
import {useNavigate} from 'react-router-dom'
import {Pencil, Plus} from 'lucide-react'
import {Avatar} from '../components/common/Avatar'
import {SearchBar} from '../components/common/SearchBar'
import {SummaryCard} from '../components/common/SummaryCard'
import {TagBadge} from '../components/common/TagBadge'
import {useSupabaseSubscription} from '../app/useSupabaseSubscription'

interface PersonnelRow {
  id: string
  name: string
  employee_id: string
  designation: string | null
  crew: string | null
  status: 'tagged_in' | 'tagged_out' | 'missing'
  shift_id: string | null
  location: string | null
  photo: string | null
}

interface TagRow {
  id: string
  tag_id: string
  assigned_to_id: string | null
  assigned_to_type: string | null
  battery_level: number
  signal_strength: string
  is_connected: boolean
}

export const PersonnelRoster: React.FC = () => {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')

  const {rows: personnel, loading: pLoading, error: pError} =
    useSupabaseSubscription<PersonnelRow>({
      table: 'personnel',
      // The realtime subscription only delivers UPDATE here, but the listing
      // also needs INSERT/DELETE so newly-created personnel appear without a
      // page reload. We accept all 3 events.
      columns: 'id, name, employee_id, designation, crew, status, shift_id, location, photo',
      orderBy: ['name', true],
    })

  const {rows: tags, loading: tLoading, error: tError} =
    useSupabaseSubscription<TagRow>({
      table: 'tags',
      columns: 'id, tag_id, assigned_to_id, assigned_to_type, battery_level, signal_strength, is_connected',
      scopedToMine: false,
    })

  // Bucket tags by personnel id once, then look up per-row.
  const tagsByPersonnel = useMemo(() => {
    const map = new Map<string, TagRow[]>()
    for (const t of tags) {
      if (t.assigned_to_type !== 'personnel' || !t.assigned_to_id) continue
      const arr = map.get(t.assigned_to_id) ?? []
      arr.push(t)
      map.set(t.assigned_to_id, arr)
    }
    return map
  }, [tags])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return personnel
    return personnel.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.employee_id.toLowerCase().includes(q) ||
      (p.designation ?? '').toLowerCase().includes(q),
    )
  }, [personnel, search])

  const summary = useMemo(() => {
    // Mirror mobile's personnelSlice.fetchPersonnel summary formulas so
    // web and tablet show the same counts for the same Supabase dataset.
    // Active Tags = personnel with at least one *connected* tag (not just
    // "has any tag"). Alerts = personnel with any tag whose battery < 30
    // OR which is disconnected. "missing" status is not factored in here.
    const total = personnel.length
    let withTags = 0
    let alerts = 0
    for (const p of personnel) {
      const t = tagsByPersonnel.get(p.id)
      if (t && t.some(tt => tt.is_connected)) withTags++
      if (t && t.some(tt => tt.battery_level < 30 || !tt.is_connected)) alerts++
    }
    return {total, withTags, alerts}
  }, [personnel, tagsByPersonnel])

  if (pLoading || tLoading) {
    return <div className="flex h-full items-center justify-center text-textMuted">Loading personnel…</div>
  }
  if (pError || tError) {
    return (
      <div className="flex h-full items-center justify-center p-xxxl">
        <div className="rounded-lg border border-statusRed bg-surfaceCard p-xxl">
          <h3 className="mb-md text-lg font-semibold text-statusRed">Fetch failed</h3>
          <pre className="whitespace-pre-wrap font-mono text-sm text-textPrimary">{pError || tError}</pre>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      {/* Toolbar — search inside surface card (consistent across all list
          screens), Add button on the right. */}
      <div className="px-xl py-md">
        <div className="flex items-center gap-lg">
          <div className="flex flex-1 items-center gap-lg rounded-lg bg-surface p-sm">
            <div className="flex-1">
              <SearchBar value={search} onChange={setSearch} placeholder="Search.." />
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/personnel/add')}
            className="flex items-center gap-sm rounded-md bg-primary px-xl py-md text-md font-bold text-black hover:bg-primaryLight"
          >
            <Plus size={16} color="black" strokeWidth={3} />
            Add Personnel
          </button>
        </div>
      </div>

      {/* Summary cards — px-xl gap-md mb-lg (matches mobile.summaryRow).
          Left accent stripes match the colored bar style used on mobile. */}
      <div className="mb-lg flex gap-md px-xl">
        <SummaryCard
          label="Total Employees"
          value={summary.total}
          accentColor="var(--color-primary)"
        />
        <SummaryCard
          label="Active Tags"
          value={summary.withTags}
          valueClass="text-statusGreen"
          accentColor="var(--color-statusGreen)"
        />
        <SummaryCard
          label="Alerts"
          value={summary.alerts}
          valueClass="text-statusRed"
          accentColor="var(--color-statusRed)"
        />
      </div>

      {/* Table — flex column so the scroll body sizes to remaining space.
          Previously used h-[calc(100%-44px)] which clipped the last row when
          the header row's intrinsic height varied (text padding + border). */}
      <div className="mx-xl mb-xl flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-surfaceBorder bg-surfaceCard">
        <div className="grid shrink-0 grid-cols-[70px_1.5fr_1.2fr_1.5fr_60px] items-center border-b border-divider bg-surface px-lg py-md text-xs font-semibold uppercase text-textMuted">
          <div>Avatar</div>
          <div>Name &amp; Emp ID</div>
          <div>Tag ID &amp; Status</div>
          <div>Other Tags</div>
          <div className="text-center">Actions</div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filtered.map(p => {
            const personTags = tagsByPersonnel.get(p.id) ?? []
            const primary = personTags[0]
            const others = personTags.slice(1)
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => navigate(`/personnel/${p.id}`)}
                className={clsx(
                  'grid w-full grid-cols-[70px_1.5fr_1.2fr_1.5fr_60px] items-center px-lg py-md text-left',
                  'border-b border-divider hover:bg-surfaceLight/40 focus:bg-surfaceLight/60 focus:outline-none',
                )}
              >
                <div>
                  <Avatar uri={p.photo} name={p.name} size={40} />
                </div>
                <div className="min-w-0">
                  <div className="truncate font-semibold text-textPrimary">{p.name}</div>
                  <div className="truncate text-xs text-textMuted">{p.employee_id}</div>
                </div>
                <div>
                  {primary ? (
                    <TagBadge tagId={primary.tag_id} isActive={primary.is_connected} />
                  ) : (
                    <span className="text-sm italic text-textMuted">No tag</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-xs">
                  {others.length === 0 ? (
                    <span className="text-md text-textMuted">—</span>
                  ) : (
                    others.map(t => (
                      <TagBadge key={t.id} tagId={t.tag_id} isActive={t.is_connected} />
                    ))
                  )}
                </div>
                <div className="flex justify-center">
                  <span
                    role="button"
                    tabIndex={-1}
                    onClick={ev => {
                      ev.stopPropagation()
                      navigate(`/personnel/${p.id}/edit`)
                    }}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-surfaceBorder text-textPrimary hover:border-primary hover:text-primary"
                    aria-label={`Edit ${p.name}`}
                  >
                    <Pencil size={14} />
                  </span>
                </div>
              </button>
            )
          })}
          {filtered.length === 0 && (
            <div className="px-lg py-xxxl text-center text-textMuted">
              No personnel match "{search}".
            </div>
          )}
        </div>
      </div>

      <div className="h-md" />
    </div>
  )
}
